import React from 'react';
import omitBy from 'lodash/fp/omitBy';
import isNil from 'lodash/fp/isNil';

import {
	ButtonOptions,
	CardExpiry,
	CardNumber,
	DatePicker,
	Dropdown,
	Quantity,
	RadioButtons,
	TextArea,
	TextField,
	TimeField,
	Checkbox,
	PhoneNumber,
} from '../FormComponents';
import { INPUT_TYPES } from '@micro-apps/shared';
import { isMobileDevice } from '@micro-apps/env';

export default function FieldSelector({
	type = INPUT_TYPES.TEXT,
	onChange,
	additionalNames = [],
	formikProps = {},
	labelFn = label => label,
	flexDirection = 'column',
	radioIsButton = false,
	circularIcons = true,
	allowBlank = true,

	field: _ignoredField,
	form: _ignoredForm,

	styles,

	content,
	countriesDialCode = [],

	...initialProps
}) {
	const props = omitBy(isNil)(initialProps);

	// eslint-disable-next-line @typescript-eslint/no-empty-function, react-hooks/exhaustive-deps
	const setFieldValue = React.useCallback(formikProps?.setFieldValue || (() => {}), [formikProps]);

	const countries = countriesDialCode.length ? countriesDialCode : content?.countries;

	const setAdditionalFieldValues = React.useCallback(
		valueToSet => {
			additionalNames.forEach(fieldName => setFieldValue(fieldName, valueToSet));
		},
		[additionalNames, setFieldValue]
	);

	const handleChange = e => {
		onChange(e);
		setAdditionalFieldValues(e.target.value);
	};

	//Need to set the additionalNames for inital values as well, useEffect so it only does so if the value changes not every time the user enters a value which causes an infinite loop
	React.useEffect(() => {
		if (props?.value) {
			setAdditionalFieldValues(props?.value);
		}
	}, [props?.value, setAdditionalFieldValues]);

	if (
		type === INPUT_TYPES.NUMBER &&
		(props?.name?.endsWith('Quantity') || props?.name?.includes('Number_of'))
	) {
		return <Quantity {...props} circularIcons={circularIcons} onChange={handleChange} />;
	}

	if (type === INPUT_TYPES.PHONE && countries) {
		return <PhoneNumber countries={countries} {...props} onChange={handleChange} />;
	}

	switch (type) {
		case INPUT_TYPES.BUTTON_OPTIONS:
			return <ButtonOptions {...props} onChange={handleChange} />;

		case INPUT_TYPES.DROPDOWN:
			return (
				<Dropdown labelFn={labelFn} {...props} onChange={handleChange} allowBlank={allowBlank} />
			);

		case INPUT_TYPES.CARD_EXPIRY_DATE:
			return <CardExpiry {...props} onChange={handleChange} />;

		case INPUT_TYPES.CARD_NUMBER:
			return <CardNumber {...props} onChange={handleChange} />;

		case INPUT_TYPES.TEXTAREA:
			return <TextArea {...props} onChange={handleChange} />;

		case INPUT_TYPES.RADIO:
			return (
				<RadioButtons
					flexDirection={flexDirection}
					radioIsButton={radioIsButton}
					labelFn={labelFn}
					{...props}
					allowBlank
					onChange={handleChange}
				/>
			);

		case INPUT_TYPES.DATE_PICKER:
			return <DatePicker onChange={handleChange} {...props} />;

		case INPUT_TYPES.DATE:
			return <TextField type={type} forceLabel onChange={handleChange} {...props} />;

		case INPUT_TYPES.CHECKBOX:
			return (
				<Checkbox onChange={handleChange} styles={styles} {...props}>
					{props?.label}
				</Checkbox>
			);

		case INPUT_TYPES.TIME:
			if (!isMobileDevice()) {
				return <TimeField onChange={handleChange} {...props} />;
			}
			return <TextField type={type} {...props} forceLabel onChange={handleChange} />;

		default:
			return <TextField type={type} {...props} onChange={handleChange} />;
	}
}
